/**
 * Module dependencies.
 */

import { useRequestProvider } from 'app/components/providers/request-provider/request-provider';
import { resolveAppUrl } from 'app/core/utils/url-resolver';
import { AxiosInstance } from 'axios';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { ProjectShare } from 'app/types/project';

/**
 * Get project shares.
 */

function getProjectShares(client: AxiosInstance, projectId: string): Promise<ProjectShare[]> {
  return client.get(resolveAppUrl('projectShares', { interpolations: { projectId } })).then(({ data }) => data?.data);
}

/**
 * Export `useProjectSharesCacheInvalidator`.
 */

export function useProjectSharesCacheInvalidator(projectId: string) {
  const queryClient = useQueryClient();

  return useCallback(() => {
    queryClient.invalidateQueries(['projectShares', projectId]);
  }, [queryClient, projectId]);
}

/**
 * Export `useProjectShares`.
 */

export function useProjectShares(projectId: string) {
  const client = useRequestProvider();

  return useQuery(['projectShares', projectId], {
    queryFn: async () => {
      return await getProjectShares(client, projectId);
    }
  });
}
